import React, { useEffect, useState, useCallback } from "react";
import { PlxsButton } from "@plexlogic/plexus-ui";
import config from "../../config";
import { API_ENDPOINTS, getAccessToken, apiService } from "../../services/api";
import { getMaxFileSize } from "../../utils/configUtils";
import { toSentenceCase } from "../../utils/stringUtils";
import { brand, positive, neutral } from "../../theme/styles/color";

// Import Uppy React integration
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import { Dashboard, useUppy } from "@uppy/react";

// Import Uppy styles
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";

// Import Uppy types
import type { UppyFile } from "@uppy/core";

// Helper function to extract error message from HTTP response
const extractHttpErrorMessage = async (response: Response): Promise<string> => {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();
      if (errorData.detail) {
        return errorData.detail;
      }
      if (errorData.message) {
        return errorData.message;
      }
      if (errorData.error) {
        return errorData.error;
      }
    }
    // Fallback to text response
    const errorText = await response.text();
    return errorText || response.statusText;
  } catch {
    // If parsing fails, return status text
    return response.statusText || `HTTP ${response.status} error`;
  }
};

// Component props interface
interface UppyUploadComponentProps {
  drawId: string;
  initialFile?: File;
  onUploadComplete?: (entriesUpload: any) => void;
  onUploadError?: (error: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
}

const UppyUploadComponent: React.FC<UppyUploadComponentProps> = ({
  drawId,
  initialFile,
  onUploadComplete,
  onUploadError,
  onCancel,
  disabled = false,
}) => {
  const [currentFiles, setCurrentFiles] = useState<UppyFile[]>([]);

  // TODO: Consider migrating to UppyContextProvider pattern for complex apps
  // Current useUppy hook approach is still supported and appropriate for single component usage
  // See: https://uppy.io/docs/react/#uppycontextprovider for more complex scenarios
  const uppy = useUppy(() => {
    return new Uppy({
      // Global retry configuration
      autoProceed: false, // Don't auto-proceed to allow manual retry control
      allowMultipleUploads: false, // Only one upload at a time for large files
      // Enable debug logging to help with troubleshooting
      debug: false,
      restrictions: {
        maxNumberOfFiles: 1,
        maxFileSize: getMaxFileSize(), // Configurable file size limit
        allowedFileTypes: [
          ".csv",
          ".txt",
          "text/csv",
          "application/csv",
          "text/plain",
        ],
      },
    }).use(AwsS3, {
      // Always use multipart uploads for all files (UppyUploadComponent is only for large files)
      shouldUseMultipart: () => true,
      limit: 1, // One upload at a time for large files

      // Retry configuration for failed uploads - reduce aggressive retries to prevent file data loss
      retryDelays: [1000, 3000], // Retry after 1s, then 3s (reduced from 4 attempts to 2)

      // Chunk size for multipart uploads (10MB chunks)
      getChunkSize: (file: UppyFile) =>
        Math.max(10 * 1024 * 1024, 5 * 1024 * 1024), // 10MB, min 5MB for S3

      // Use without companion - custom multipart functions
      createMultipartUpload: async (file: UppyFile) => {
        // Validate file data is available
        if (!file || !file.data) {
          throw new Error("File data is not available for upload");
        }

        if (config.features.anonymousDraftsEnabled) {
          const data = await apiService.multipartInitiate(drawId, {
            original_filename: file.name,
            content_type: file.type || "text/csv",
            file_size: file.size,
          });
          return { uploadId: data.uploadId, key: data.storedFileName };
        }

        const searchParams = new URLSearchParams({
          original_filename: file.name,
          content_type: file.type || "text/csv",
          file_size: file.size.toString(),
        });

        try {
          // Get fresh access token for this request
          const token = await getAccessToken();
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }

          const response = await fetch(
            `${config.api.baseURL}${API_ENDPOINTS.MULTIPART_INITIATE(drawId)}?${searchParams}`,
            {
              method: "POST",
              headers,
            }
          );

          if (!response.ok) {
            const errorMessage = await extractHttpErrorMessage(response);
            throw new Error(
              `Failed to initiate upload (${response.status}): ${errorMessage}`
            );
          }

          const data = await response.json();
          return {
            uploadId: data.uploadId,
            key: data.storedFileName,
          };
        } catch (error) {
          // Error logged in catch block
          throw error;
        }
      },

      signPart: async (file: UppyFile, partData: any) => {
        // Validate file and part data
        if (!file) {
          throw new Error("File reference is null during signPart");
        }
        if (
          !partData ||
          !partData.key ||
          !partData.partNumber ||
          !partData.uploadId
        ) {
          throw new Error("Invalid part data for signing");
        }

        if (config.features.anonymousDraftsEnabled) {
          const data = await apiService.multipartGetPartUrl(
            drawId,
            partData.key,
            partData.partNumber,
            partData.uploadId
          );
          return { url: data.presignedUrl, headers: data.headers || {} };
        }

        try {
          // Get fresh access token for each part
          const token = await getAccessToken();
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }

          const response = await fetch(
            `${config.api.baseURL}${API_ENDPOINTS.MULTIPART_PART_URL(drawId, partData.key, partData.partNumber)}?upload_id=${partData.uploadId}`,
            {
              method: "GET",
              headers,
            }
          );

          if (!response.ok) {
            const errorMessage = await extractHttpErrorMessage(response);
            throw new Error(
              `Failed to get signed URL (${response.status}): ${errorMessage}`
            );
          }

          const data = await response.json();
          return {
            url: data.presignedUrl,
            headers: data.headers || {},
          };
        } catch (error) {
          // Error logged in catch block
          throw error;
        }
      },

      completeMultipartUpload: async (
        file: UppyFile,
        { uploadId, key, parts }: any
      ) => {
        const transformedParts = parts.map((part: any) => ({
          partNumber: part.PartNumber,
          etag: part.ETag,
        }));

        if (config.features.anonymousDraftsEnabled) {
          const data = await apiService.multipartComplete(drawId, key, {
            uploadId,
            parts: transformedParts,
          });
          return {
            location: `https://${data.s3Bucket}.s3.amazonaws.com/${data.s3Key}`,
            body: data,
          };
        }

        // Get fresh access token for completion
        const token = await getAccessToken();
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await fetch(
          `${config.api.baseURL}${API_ENDPOINTS.MULTIPART_COMPLETE(drawId, key)}`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              uploadId,
              parts: transformedParts,
            }),
          }
        );

        if (!response.ok) {
          const errorMessage = await extractHttpErrorMessage(response);
          throw new Error(
            `Failed to complete upload: ${response.statusText} - ${errorMessage}`
          );
        }

        const data = await response.json();
        return {
          location: `https://${data.s3Bucket}.s3.amazonaws.com/${data.s3Key}`,
          body: data,
        };
      },

      listParts: async () => {
        return [];
      },

      abortMultipartUpload: async (file: UppyFile, { uploadId, key }: any) => {
        if (config.features.anonymousDraftsEnabled) {
          await apiService.multipartAbort(drawId, key, uploadId);
          return;
        }

        // Get fresh access token for abort
        const token = await getAccessToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        await fetch(
          `${config.api.baseURL}${API_ENDPOINTS.MULTIPART_ABORT(drawId, key)}?upload_id=${uploadId}`,
          {
            method: "DELETE",
            headers,
          }
        );
      },
    } as any);
  });

  // Set up event listeners
  useEffect(() => {
    // Helper function to validate file data integrity
    const validateFileData = (file: UppyFile): boolean => {
      if (!file || !file.data) return false;

      return (
        file.data instanceof File ||
        file.data instanceof Blob ||
        (typeof file.data === "object" &&
          "size" in file.data &&
          (file.data as any).size > 0)
      );
    };

    const handleFileAdded = (file: UppyFile) => {
      // Validate file data before processing
      if (!validateFileData(file)) {
        if (onUploadError) {
          onUploadError(
            "Invalid file data - please try selecting the file again"
          );
        }
        return;
      }

      setCurrentFiles([file]);
      // Automatically start upload when file is added
      if (!disabled) {
        uppy.upload();
      }
    };

    const handleFileRemoved = (file: UppyFile) => {
      setCurrentFiles([]);
    };

    const handleUploadProgress = (
      file: UppyFile | undefined,
      progress: { bytesUploaded: number; bytesTotal: number }
    ) => {
      // Progress is now handled by Uppy Dashboard UI
      // No need to track progress separately
    };

    const handleUploadError = (file: UppyFile | undefined, error: Error) => {
      // Upload error logged for debugging

      // Check if error is related to file data issues
      if (error.message.includes("getData") || error.message.includes("null")) {
        // File data became null - this is common in retry scenarios
        if (file && !validateFileData(file)) {
          if (onUploadError) {
            onUploadError(
              "File data lost during upload. Please try re-selecting the file."
            );
          }
          return;
        }
      }

      if (onUploadError) {
        onUploadError(error.message || toSentenceCase("upload failed"));
      }
    };

    const handleComplete = (result: any) => {
      if (result.successful && result.successful.length > 0) {
        const successfulFile = result.successful[0];
        // Extract response data from the successful upload
        const responseData =
          successfulFile.response?.body || successfulFile.response;

        if (onUploadComplete && responseData) {
          onUploadComplete(responseData);
        }
      } else if (result.failed && result.failed.length > 0) {
        // Handle failed uploads
        const failedFile = result.failed[0];
        if (onUploadError) {
          onUploadError(
            failedFile.error?.message || toSentenceCase("upload failed")
          );
        }
      }
    };

    const handleCancelAll = () => {
      setCurrentFiles([]);
      if (onCancel) {
        onCancel();
      }
    };

    // Retry functions removed - automatic retries handled by AWS S3 plugin configuration

    // Add event listeners
    uppy.on("file-added", handleFileAdded);
    uppy.on("file-removed", handleFileRemoved);
    uppy.on("upload-progress", handleUploadProgress);
    uppy.on("upload-error", handleUploadError);
    uppy.on("complete", handleComplete);
    uppy.on("cancel-all", handleCancelAll);

    // Add upload start handler to validate file data before upload begins
    uppy.on("upload", (data) => {
      if (data && data.fileIDs) {
        data.fileIDs.forEach((fileId: string) => {
          const file = uppy.getFile(fileId);
          if (!validateFileData(file)) {
            // Cancel upload for this file to prevent getData null errors
            uppy.removeFile(fileId);
            onUploadError?.(
              `File data became invalid before upload: ${
                file?.name || "unknown"
              }`
            );
          }
        });
      }
    });

    // Cleanup event listeners
    return () => {
      uppy.off("file-added", handleFileAdded);
      uppy.off("file-removed", handleFileRemoved);
      uppy.off("upload-progress", handleUploadProgress);
      uppy.off("upload-error", handleUploadError);
      uppy.off("complete", handleComplete);
      uppy.off("cancel-all", handleCancelAll);
    };
  }, [uppy, disabled, onUploadComplete, onUploadError, onCancel]);

  // Handle initial file if provided
  useEffect(() => {
    if (initialFile && currentFiles.length === 0) {
      try {
        uppy.addFile({
          name: initialFile.name,
          type: initialFile.type,
          data: initialFile,
          source: "file input",
          isRemote: false,
        });
      } catch (err) {
        if (onUploadError) {
          onUploadError(toSentenceCase("failed to add initial file"));
        }
      }
    }
  }, [uppy, initialFile, currentFiles.length, onUploadError]);

  const handleCancelClick = useCallback(() => {
    uppy.cancelAll();
  }, [uppy]);

  const handleRemoveFiles = useCallback(() => {
    currentFiles.forEach((file) => {
      uppy.removeFile(file.id);
    });
  }, [uppy, currentFiles]);

  return (
    <div className="uppy-upload-wrapper">
      {/* Inline styles for Plexus design system integration */}
      <style>{`
        .uppy-Dashboard-inner {
          background-color: ${neutral[900]} !important;
          border: 2px solid ${neutral[600]} !important;
          border-radius: 8px !important;
        }
        .uppy-DashboardContent-bar {
          background-color: ${neutral[800]} !important;
          border-bottom: 1px solid ${neutral[600]} !important;
        }
        .uppy-DashboardContent-addMore {
          color: ${brand[400]} !important;
        }
        .uppy-DashboardContent-addMore:hover {
          color: ${brand[300]} !important;
        }
        .uppy-Dashboard-browse {
          color: ${brand[400]} !important;
          text-decoration: underline !important;
        }
        .uppy-Dashboard-browse:hover {
          color: ${brand[300]} !important;
        }
        .uppy-ProgressBar {
          background-color: ${neutral[700]} !important;
        }
        .uppy-ProgressBar-fill {
          background-color: ${positive[400]} !important;
        }
        .uppy-StatusBar {
          background-color: ${neutral[800]} !important;
          border-top: 1px solid ${neutral[600]} !important;
        }
        .uppy-StatusBar-actionBtn--upload {
          background-color: ${brand[400]} !important;
          color: white !important;
        }
        .uppy-StatusBar-actionBtn--upload:hover {
          background-color: ${brand[300]} !important;
        }
        .uppy-StatusBar-actionBtn--done {
          background-color: ${positive[400]} !important;
          color: white !important;
        }
        .uppy-StatusBar-actionBtn--retry {
          background-color: ${brand[400]} !important;
          color: white !important;
        }
        .uppy-StatusBar-actionBtn--retry:hover {
          background-color: ${brand[300]} !important;
        }
        .uppy-Dashboard-Item-name {
          color: ${neutral[100]} !important;
        }
        .uppy-Dashboard-Item-status {
          color: ${neutral[200]} !important;
        }
        .uppy-DashboardContent-title {
          color: ${neutral[100]} !important;
        }
      `}</style>
      
      {/* Uppy Dashboard with Plexus styling */}
      <Dashboard
        uppy={uppy}
        plugins={["AwsS3"]}
        metaFields={[]}
        width="100%"
        height="400px"
        showProgressDetails={true}
        showSelectedFiles={true}
        hideUploadButton={true} // Auto-upload when file is added
        hideRetryButton={false}
        hidePauseResumeButton={true} // Disable pause/resume as per requirements
        hideCancelButton={false}
        showRemoveButtonAfterComplete={true}
        note={`${toSentenceCase(
          "upload large CSV files (up to 2GB). Files start uploading automatically with chunking for reliability."
        )}.`}
        proudlyDisplayPoweredByUppy={false}
        locale={{
          strings: {
            dropPasteImportBoth: `${toSentenceCase(
              "drop files here, paste or %{browseFiles}"
            )}`,
            dropPasteImportFiles: `${toSentenceCase(
              "drop files here, paste or %{browseFiles}"
            )}`,
            browseFiles: toSentenceCase("browse files"),
            uploadComplete: toSentenceCase("upload complete"),
            retryUpload: toSentenceCase("retry upload"),
            cancelUpload: toSentenceCase("cancel upload"),
            xFilesSelected: {
              0: toSentenceCase("%{smart_count} file selected"),
              1: toSentenceCase("%{smart_count} files selected"),
            },
            uploadingXFiles: {
              0: toSentenceCase("uploading %{smart_count} file"),
              1: toSentenceCase("uploading %{smart_count} files"),
            },
            processingXFiles: {
              0: toSentenceCase("processing %{smart_count} file"),
              1: toSentenceCase("processing %{smart_count} files"),
            },
          },
        }}
        disabled={disabled}
      />

      {/* Control buttons with Plexus spacing */}
      {currentFiles.length > 0 && (
        <div style={{ 
          marginTop: "1.5rem", 
          display: "flex", 
          gap: "0.75rem", 
          alignItems: "center",
          justifyContent: "flex-start"
        }}>
          <PlxsButton
            dataTestId="cancel-upload-button"
            label={toSentenceCase("cancel upload")}
            tone="neutral"
            onClick={handleCancelClick}
            variant="secondary"
          />
          <PlxsButton
            dataTestId="remove-files-button"
            label={toSentenceCase("remove file")}
            onClick={handleRemoveFiles}
          />
        </div>
      )}
    </div>
  );
};

export default UppyUploadComponent;
